<template>
  <div v-loading="loading">
    <el-form label-position="top">
      <el-form-item label="选择触发动作">
        <el-select v-model="nodeProps.triggerType" placeholder="请选择触发器" style="width:100%" @change="onTriggerChange">
          <el-option v-for="schema in schemas" :key="schema.code" :label="schema.name" :value="schema.code" />
        </el-select>
        <div v-if="currentSchema && currentSchema.description" class="field-hint">
          {{ currentSchema.description }}
        </div>
      </el-form-item>

      <template v-if="currentSchema">
        <template v-for="field in currentSchema.fields">
          <!-- text / textarea / number -->
          <el-form-item v-if="isSimpleInput(field.type)" :key="field.key" :label="field.label">
            <el-input
              v-model="nodeProps.triggerParams[field.key]"
              :type="field.type === 'textarea' ? 'textarea' : 'text'"
              :placeholder="field.placeholder"
              :rows="field.type === 'textarea' ? 3 : undefined"
            />
            <div v-if="field.hint" class="field-hint">{{ field.hint }}</div>
          </el-form-item>

          <!-- select -->
          <el-form-item v-else-if="field.type === 'select'" :key="field.key" :label="field.label">
            <el-select v-model="nodeProps.triggerParams[field.key]" :placeholder="field.placeholder" style="width:100%">
              <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value" />
            </el-select>
            <div v-if="field.hint" class="field-hint">{{ field.hint }}</div>
          </el-form-item>

          <!-- user-picker -->
          <el-form-item v-else-if="field.type === 'user-picker'" :key="field.key" :label="field.label">
            <el-button size="mini" type="primary" icon="el-icon-plus" round @click="openPicker(field.key, 'user')">选择人员</el-button>
            <member-items v-model="nodeProps.triggerParams[field.key]" />
            <member-picker
              :ref="`picker_${field.key}`"
              multiple
              type="user"
              :selected="nodeProps.triggerParams[field.key] || []"
              @ok="val => onPickerOk(field.key, val)"
            />
            <div v-if="field.hint" class="field-hint">{{ field.hint }}</div>
          </el-form-item>

          <!-- role-picker -->
          <el-form-item v-else-if="field.type === 'role-picker'" :key="field.key" :label="field.label">
            <el-button size="mini" type="primary" icon="el-icon-plus" round @click="openPicker(field.key, 'role')">选择角色</el-button>
            <member-items v-model="nodeProps.triggerParams[field.key]" />
            <member-picker
              :ref="`picker_${field.key}`"
              multiple
              type="role"
              :selected="nodeProps.triggerParams[field.key] || []"
              @ok="val => onPickerOk(field.key, val)"
            />
            <div v-if="field.hint" class="field-hint">{{ field.hint }}</div>
          </el-form-item>

          <!-- list：动态添加行 -->
          <el-form-item v-else-if="field.type === 'list'" :key="field.key">
            <div slot="label">
              <span style="margin-right:10px">{{ field.label }}</span>
              <el-button type="text" @click="addListRow(field.key, field.columns)">+ 添加</el-button>
            </div>
            <div class="list-rows">
              <div
                v-for="(row, rowIdx) in (nodeProps.triggerParams[field.key] || [])"
                :key="rowIdx"
                class="list-row"
              >
                <el-input
                  v-for="col in field.columns"
                  :key="col.key"
                  v-model="row[col.key]"
                  :placeholder="col.label"
                  size="small"
                  class="list-col"
                />
                <el-button type="text" icon="el-icon-delete" class="list-del" @click="removeListRow(field.key, rowIdx)" />
              </div>
            </div>
            <div v-if="field.hint" class="field-hint">{{ field.hint }}</div>
          </el-form-item>
        </template>
      </template>
    </el-form>
  </div>
</template>

<script>
import MemberPicker from '../../common/MemberPicker'
import MemberItems from '../MemberItems'

export default {
  name: 'TriggerNodeConfig',
  inject: ['$workflow'],
  components: { MemberPicker, MemberItems },
  props: {
    config: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      schemas: []
    }
  },
  computed: {
    nodeProps () {
      return this.$workflow.selectedNode.props
    },
    currentSchema () {
      return this.schemas.find(s => s.code === this.nodeProps.triggerType) || null
    }
  },
  watch: {
    'nodeProps.triggerParams': {
      deep: true,
      handler () {
        this.validateParams()
      }
    }
  },
  mounted () {
    this.loadSchemas()
    this.initDefaultProps()
  },
  methods: {
    initDefaultProps () {
      const props = this.nodeProps
      if (props.triggerParams === undefined) {
        this.$set(props, 'triggerParams', {})
      }
      if (props.triggerName === undefined) {
        this.$set(props, 'triggerName', '')
      }
    },
    validateParams () {
      const props = this.nodeProps
      const schema = this.currentSchema
      if (schema && schema.fields) {
        const params = props.triggerParams || {}
        for (const field of schema.fields) {
          if (!field.required) continue
          const value = params[field.key]
          if (value === undefined || value === null || value === '') {
            this.$set(props, 'triggerError', `请填写${field.label}`)
            return
          }
        }
      }
      this.$delete(props, 'triggerError')
    },
    loadSchemas () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/flow/configure/approval/trigger',
        method: 'post'
      }).then(res => {
        this.schemas = res.data.result || []
        this.validateParams()
      }).finally(() => {
        this.loading = false
      })
    },
    onTriggerChange (code) {
      const props = this.nodeProps
      this.$set(props, 'triggerParams', {})
      const schema = this.schemas.find(s => s.code === code)
      this.$set(props, 'triggerName', schema ? schema.name : '')
    },
    isSimpleInput (type) {
      return ['text', 'textarea', 'number'].includes(type)
    },
    openPicker (fieldKey) {
      const ref = this.$refs[`picker_${fieldKey}`]
      const picker = Array.isArray(ref) ? ref[0] : ref
      if (picker) picker.show()
    },
    onPickerOk (fieldKey, selected) {
      this.$set(this.nodeProps.triggerParams, fieldKey, [...selected])
    },
    addListRow (fieldKey, columns) {
      if (!this.nodeProps.triggerParams[fieldKey]) {
        this.$set(this.nodeProps.triggerParams, fieldKey, [])
      }
      const emptyRow = {}
      columns.forEach(col => { emptyRow[col.key] = '' })
      this.nodeProps.triggerParams[fieldKey].push(emptyRow)
    },
    removeListRow (fieldKey, idx) {
      this.nodeProps.triggerParams[fieldKey].splice(idx, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.field-hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 8px;
}

.list-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.list-col {
  flex: 1;
}

.list-del {
  color: #c75450;
}
</style>
